/**
 * URL-style connection-string parser.
 *   postgres://, postgresql:// → pgsql
 *   mysql://, mariadb://       → mysql
 *
 * Example: postgres://user:p%40ss@db.internal:5432/orders?sslmode=require
 */

import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import type { ParserMatch, ConnectionStringParser } from './types';
import { safeDecode } from './helpers';

const SCHEME_ENGINE: Record<string, DatabaseEngine> = {
  postgres: 'pgsql',
  postgresql: 'pgsql',
  mysql: 'mysql',
  mariadb: 'mysql',
};

export const parseUrlStyle: ConnectionStringParser = (input) => {
  const trimmed = input.trim();
  const schemeMatch = /^([a-z]+):\/\//i.exec(trimmed);
  if (!schemeMatch) return null;
  const scheme = schemeMatch[1].toLowerCase();
  const engine = SCHEME_ENGINE[scheme];
  if (!engine) return null;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  const values: Partial<ConnectionFormValues> = { engine };
  const matchedFields: string[] = [];
  const unmatchedKeys: string[] = [];

  if (url.hostname) {
    values.server = safeDecode(url.hostname);
    matchedFields.push('server');
  }
  if (url.port) {
    values.port = url.port;
    matchedFields.push('port');
  }
  const database = url.pathname.replace(/^\//, '');
  if (database) {
    values.database = safeDecode(database);
    matchedFields.push('database');
  }
  if (url.username) {
    values.login = safeDecode(url.username);
    matchedFields.push('user');
  }
  if (url.password) {
    values.password = safeDecode(url.password);
    matchedFields.push('password');
  }

  // SSL: pg uses `sslmode`; mysql commonly `ssl-mode` / `sslmode` / `useSSL`.
  const sslParamKeys = ['sslmode', 'ssl-mode', 'ssl_mode', 'usessl', 'ssl'];
  for (const [key, rawVal] of url.searchParams.entries()) {
    const lk = key.toLowerCase();
    if (sslParamKeys.includes(lk)) {
      const v = rawVal.toLowerCase();
      const sslOn = v !== 'disable' && v !== 'disabled' && v !== 'false' && v !== '0' && v !== 'none';
      if (sslOn) {
        values.ssl = true;
        matchedFields.push('SSL');
      } else {
        values.ssl = false;
      }
    } else if (lk === 'user' || lk === 'username') {
      values.login = safeDecode(rawVal);
      if (!matchedFields.includes('user')) matchedFields.push('user');
    } else if (lk === 'password') {
      values.password = safeDecode(rawVal);
      if (!matchedFields.includes('password')) matchedFields.push('password');
    } else if (lk === 'dbname' || lk === 'database') {
      if (!values.database) {
        values.database = safeDecode(rawVal);
        matchedFields.push('database');
      }
    } else {
      unmatchedKeys.push(key);
    }
  }

  const match: ParserMatch = {
    format: 'url',
    engine,
    values,
    matchedFields,
    unmatchedKeys,
  };
  return match;
};
